/**
 * Load a remote profile photo for canvas export (requires CORS-safe pixels).
 * Tries direct load, fetch-as-blob, then a CORS-friendly image proxy.
 */

function loadViaImageTag(src, { crossOrigin, referrerPolicy } = {}) {
  if (!src) return Promise.resolve(null);

  return new Promise((resolve) => {
    const img = new Image();
    if (crossOrigin) img.crossOrigin = crossOrigin;
    if (referrerPolicy) img.referrerPolicy = referrerPolicy;
    img.decoding = "async";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

async function loadViaFetchBlob(src) {
  try {
    const response = await fetch(src, {
      mode: "cors",
      credentials: "omit",
      referrerPolicy: "no-referrer",
    });
    if (!response.ok) return null;
    const blob = await response.blob();
    if (!blob.size) return null;
    const objectUrl = URL.createObjectURL(blob);
    try {
      return await loadViaImageTag(objectUrl);
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  } catch {
    return null;
  }
}

/** CORS-enabled resize proxies (fallback for Twitter/CDN avatars). */
function corsProxyUrls(src) {
  const stripped = src.replace(/^https?:\/\//i, "");
  return [
    `https://wsrv.nl/?url=${encodeURIComponent(src)}&w=320&h=320&fit=cover&output=png`,
    `https://images.weserv.nl/?url=${encodeURIComponent(stripped)}&w=320&h=320&fit=cover&output=png`,
  ];
}

function isSameOriginUrl(src) {
  try {
    const url = new URL(src, window.location.href);
    return url.origin === window.location.origin;
  } catch {
    return false;
  }
}

/**
 * @param {string} src Remote or same-origin avatar URL
 * @param {HTMLImageElement|null} [domImage] Visible profile `<img>` (uses its resolved URL)
 * @returns {Promise<HTMLImageElement|null>}
 */
export async function loadProfileImageForCanvas(src, domImage = null) {
  const resolvedSrc =
    domImage?.currentSrc || domImage?.src || src || null;
  if (!resolvedSrc) return null;

  const sameOrigin = isSameOriginUrl(resolvedSrc);
  const attempts = [];

  if (sameOrigin) {
    attempts.push(() => loadViaImageTag(resolvedSrc));
  }

  attempts.push(
    () =>
      loadViaImageTag(resolvedSrc, {
        crossOrigin: "anonymous",
        referrerPolicy: "no-referrer",
      }),
    () => loadViaFetchBlob(resolvedSrc),
    ...corsProxyUrls(resolvedSrc).map(
      (proxyUrl) => () =>
        loadViaImageTag(proxyUrl, { crossOrigin: "anonymous" })
    )
  );

  for (const attempt of attempts) {
    const img = await attempt();
    if (img?.naturalWidth > 0) return img;
  }

  return null;
}
